import clsx from "clsx";
import { FC, useMemo, useRef, useState } from "react";

import type {
  ChatMessage,
  ModelCall,
  ModelEvent,
  ToolChoice,
  ToolInfo,
} from "@tsmono/inspect-common/types";
import { ChatView } from "@tsmono/inspect-components/chat";
import { MetaDataGrid } from "@tsmono/inspect-components/content";
import { ModelUsagePanel } from "@tsmono/inspect-components/usage";
import { ContentCode, CopyButton } from "@tsmono/react/components";
import { usePrismHighlight, useProperty } from "@tsmono/react/hooks";
import { Icon } from "@tsmono/react/icons";
import { formatTime } from "@tsmono/util";

import { GeneratingIndicator } from "../indicators/GeneratingIndicator";
import { isLivePlaceholderMessage } from "../indicators/livePlaceholder";

import { attemptDurationSec } from "./event/attemptDuration";
import { EventPanel } from "./event/EventPanel";
import { EventSection } from "./event/EventSection";
import { RetryChip } from "./event/RetryChip";
import { StopReasonBadge } from "./event/StopReasonBadge";
import { formatTiming, formatTitle, isCancelError } from "./event/utils";
import styles from "./ModelEventView.module.css";
import { recentInputMessages } from "./recentInputMessages";
import { retryAttemptKey } from "./timeline/retryGrouping";
import { EventNode, EventNodeContext, EventPanelCallbacks } from "./types";

interface ModelEventViewProps {
  eventNode: EventNode<ModelEvent>;
  className?: string;
  showToolCalls: boolean;
  context?: EventNodeContext;
  eventCallbacks?: EventPanelCallbacks;
}

export const ModelEventView: FC<ModelEventViewProps> = ({
  eventNode,
  showToolCalls,
  className,
  context,
  eventCallbacks,
}) => {
  const successEvent = eventNode.event;
  const attempts = context?.retryAttempts?.get(retryAttemptKey(successEvent));
  const successKey = retryAttemptKey(successEvent);

  const [selectedAttemptKey, setSelectedAttemptKey] = useProperty<string>(
    eventNode.id,
    "selectedAttempt",
    { defaultValue: successKey }
  );

  const selectedEvent =
    attempts?.find((a) => retryAttemptKey(a) === selectedAttemptKey) ??
    successEvent;
  const event = selectedEvent;
  // An operator/limit/system cancel stamps a sentinel on `error`, but it isn't
  // a genuine failure — surface it as "Cancelled", not a red "FAILED" error.
  const isCancelled = isCancelError(event.error);
  const isFailed = !!event.error && !isCancelled;

  const totalUsage = event.output.usage?.total_tokens;
  const callTime = event.output.time;

  const outputMessages = event.output.choices.map((choice) => choice.message);

  const entries: Record<string, unknown> = { ...event.config };
  delete entries["max_connections"];

  // Stop reason / refusal detail for the (primary) generated choice. `category`
  // and `explanation` are only present on a refusal/content-filter stop. Skip the
  // panel for a plain "stop" with no details — otherwise it shows on every call.
  const firstChoice = event.output.choices[0];
  const stopDetails = firstChoice?.stop_details;
  const showStopReason =
    !!firstChoice && (!!stopDetails || firstChoice.stop_reason !== "stop");

  // For any user messages which immediately preceded this model call, including a
  // panel and display those user messages (exclude tool_call messages as they
  // are already shown in the tool call above)
  const userMessages = useMemo<ChatMessage[]>(() => {
    // When agent tool results have been filtered from input (shown on AgentCard
    // instead), the trailing assistant message is the previous model call's output
    // — just show it without crawling backward through system/user messages.
    const agentResultsFiltered = !!(event as Record<string, unknown>)
      .agentResultsFiltered;

    return recentInputMessages(event.input, {
      agentResultsFiltered,
      hasToolEvents: context?.hasToolEvents,
      toolEventIds: context?.inputShownElsewhere
        ? undefined
        : context?.toolEventIds,
    });
  }, [
    event,
    context?.hasToolEvents,
    context?.toolEventIds,
    context?.inputShownElsewhere,
  ]);

  const hasHiddenMessages = event.input.length > userMessages.length;
  const [showAllMessages, setShowAllMessages] = useState(false);

  const summaryMessages = useMemo(() => {
    // Filter the synthetic empty-assistant placeholder while the event is in
    // flight, or when it was cancelled (the interrupted generation produced no
    // real output) — otherwise the same predicate would drop legitimate
    // tool_use-only outputs, since isLivePlaceholderMessage treats those as
    // "no visible content".
    const outputs =
      event.pending || isCancelled
        ? outputMessages.filter((m) => !isLivePlaceholderMessage(m))
        : outputMessages;
    const messages = showAllMessages
      ? [...event.input, ...outputs]
      : [...userMessages, ...outputs];
    const toolEventIds = context?.toolEventIds;
    return toolEventIds
      ? messages.map((m) => withoutToolCallsIn(m, toolEventIds))
      : messages;
  }, [
    showAllMessages,
    event.input,
    event.pending,
    isCancelled,
    outputMessages,
    userMessages,
    context?.toolEventIds,
  ]);

  // Folded under their calls, results the summary shows would vanish with calls it omits.
  const collapseSummaryToolMessages =
    context?.hasToolEvents !== false &&
    !userMessages.some((m) => m.role === "tool");

  const summaryLabels = useMemo(() => {
    const map = context?.messageLabels;
    if (!map) return { show: false } as const;
    return { messageLabels: map };
  }, [context?.messageLabels]);

  const panelTitle = event.role
    ? `Model Call (${event.role}): ${event.model}`
    : `Model Call: ${event.model}`;

  const titleString = isFailed
    ? `${panelTitle} · FAILED${formatFailureTime(event)}`
    : isCancelled
      ? `${panelTitle} · Cancelled${formatFailureTime(event)}`
      : formatTitle(panelTitle, totalUsage, callTime);

  const fallback = event.output.fallback;
  const fallbackBadge = fallback ? (
    <span className={styles.fallbackBadge}>
      · fallback → {fallback.fallback_model}
    </span>
  ) : undefined;

  const turnNav = context?.turnInfo
    ? {
        turnNumber: context.turnInfo.turnNumber,
        totalTurns: context.turnInfo.totalTurns,
        isAnchor: context.turnIsAnchor ?? true,
      }
    : undefined;

  const retryChip = attempts ? (
    <RetryChip
      attempts={attempts}
      selectedKey={selectedAttemptKey}
      onSelect={setSelectedAttemptKey}
      keyOf={retryAttemptKey}
    />
  ) : undefined;

  return (
    <EventPanel
      eventNodeId={eventNode.id}
      className={className}
      title={titleString}
      subTitle={
        event.timestamp
          ? formatTiming(event.timestamp, event.working_start)
          : undefined
      }
      icon="model"
      turnNav={turnNav}
      headerExtra={
        fallbackBadge || retryChip ? (
          <>
            {fallbackBadge}
            {retryChip}
          </>
        ) : undefined
      }
      eventCallbacks={eventCallbacks}
      collapsibleContent
    >
      <div data-name="Summary" className={styles.container}>
        {context?.inlineExpansionUX &&
          hasHiddenMessages &&
          !showAllMessages && (
            <div className={clsx("text-size-small", styles.showAllLink)}>
              <button
                type="button"
                onClick={() => {
                  setShowAllMessages(true);
                }}
              >
                <Icon name="chevronUp" className={styles.showAllIcon} />
                Show all messages
              </button>
            </div>
          )}
        <ChatView
          id={`${eventNode.id}-model-output`}
          messages={summaryMessages}
          tools={{
            callStyle:
              context?.toolEventIds || showToolCalls ? "complete" : "omit",
            collapseToolMessages: collapseSummaryToolMessages,
          }}
          labels={summaryLabels}
        />
        {isCancelled ? (
          <div className={styles.cancelled}>
            <Icon name="cancel" />
            <span>{event.error}</span>
          </div>
        ) : event.error ? (
          <EventSection title="Error">
            <div className={styles.error}>{event.error}</div>
          </EventSection>
        ) : event.pending ? (
          <div className={clsx(styles.progress)}>
            <GeneratingIndicator />
          </div>
        ) : undefined}
      </div>
      <div data-name="Info" className={styles.container}>
        <div className={styles.all}>
          {event.output.usage ? (
            <ModelUsagePanel
              usage={event.output.usage}
              timing={{
                timestamp: event.timestamp,
                completed: event.completed,
                working_time: event.working_time,
              }}
            />
          ) : undefined}

          {showStopReason && (
            <StopReasonBadge
              reason={firstChoice.stop_reason}
              details={stopDetails}
            />
          )}

          {Object.keys(entries).length > 0 && (
            <EventSection
              title="Configuration"
              className={clsx(styles.tableSelection, styles.config)}
            >
              <MetaDataGrid entries={entries} options={{ plain: true }} />
            </EventSection>
          )}

          <ProviderIds event={event} />
        </div>
      </div>

      <div data-name="Messages" className={styles.container}>
        <ChatView
          id={`${eventNode.id}-model-input-full`}
          messages={[...event.input, ...outputMessages]}
          tools={{
            collapseToolMessages: context?.hasToolEvents !== false,
          }}
          labels={{
            show: false,
          }}
        />
      </div>

      {event.tools.length > 1 && (
        <div data-name="Tools" className={styles.container}>
          <ToolsConfig tools={event.tools} toolChoice={event.tool_choice} />
        </div>
      )}

      {event.call ? (
        <APIView
          data-name="API"
          call={event.call}
          className={styles.container}
        />
      ) : (
        ""
      )}
    </EventPanel>
  );
};

const ProviderIds: FC<{ event: ModelEvent }> = ({ event }) => {
  const responseId = event.output.response_id;
  const requestIds = event.request_ids ?? [];
  if (!responseId && requestIds.length === 0) {
    return null;
  }

  return (
    <EventSection
      title="Provider IDs"
      className={clsx(styles.tableSelection, styles.config)}
    >
      <div className={clsx(styles.providerIds, "text-size-small")}>
        {responseId ? (
          <ProviderIdRow label="response" id={responseId} />
        ) : undefined}
        {requestIds.map((requestId, index) => (
          <ProviderIdRow
            key={`${requestId.header}-${requestId.id}-${index}`}
            label={requestId.header}
            id={requestId.id}
            status={requestId.status}
          />
        ))}
      </div>
    </EventSection>
  );
};

interface ProviderIdRowProps {
  label: string;
  id: string;
  status?: number;
}

const ProviderIdRow: FC<ProviderIdRowProps> = ({ label, id, status }) => (
  <div className={styles.providerIdRow}>
    <div className={styles.providerIdLabel}>{label}</div>
    <div className={styles.providerIdValue}>{id}</div>
    <div
      className={clsx(
        styles.providerIdStatus,
        status !== undefined && status >= 400 && styles.providerIdFailed
      )}
    >
      {status}
    </div>
    <CopyButton value={id} ariaLabel={`Copy ${label}`} />
  </div>
);

function formatFailureTime(event: ModelEvent): string {
  const sec = attemptDurationSec(event);
  return sec != null ? ` · ${formatTime(sec)}` : "";
}

interface APIViewProps {
  call: ModelCall;
  className?: string;
}

export const APIView: FC<APIViewProps> = ({ call, className }) => {
  const requestCode = useMemo(() => {
    return JSON.stringify(call.request, undefined, 2);
  }, [call.request]);

  const responseCode = useMemo(() => {
    // JSON.stringify(undefined) is undefined despite the lib typing it string.
    return call.response === undefined
      ? ""
      : JSON.stringify(call.response, undefined, 2);
  }, [call.response]);

  return (
    <div className={clsx(className)}>
      <EventSection title="Request" copyContent={requestCode}>
        <APICodeCell sourceCode={requestCode} />
      </EventSection>
      <EventSection title="Response" copyContent={responseCode}>
        <APICodeCell sourceCode={responseCode} />
      </EventSection>
    </div>
  );
};

interface APICodeCellProps {
  id?: string;
  sourceCode: string;
}

export const APICodeCell: FC<APICodeCellProps> = ({ id, sourceCode }) => {
  const sourceCodeRef = useRef<HTMLDivElement | null>(null);
  usePrismHighlight(sourceCodeRef, sourceCode.length);

  if (!sourceCode) {
    return null;
  }

  return (
    <div ref={sourceCodeRef} className={clsx("model-call")}>
      <pre className={clsx(styles.codePre)}>
        <ContentCode
          id={id}
          className={clsx("language-json", styles.code, "text-size-small")}
          text={sourceCode}
        />
      </pre>
    </div>
  );
};

interface ToolConfigProps {
  tools: ToolInfo[];
  toolChoice: ToolChoice;
}

const ToolsConfig: FC<ToolConfigProps> = ({ tools, toolChoice }) => {
  const toolEntries = useMemo<Record<string, unknown>>(() => {
    const entries: Record<string, unknown> = {};
    tools.forEach((tool, idx) => {
      // Disambiguate the rare case of two tools sharing a name (keys must be unique).
      const key =
        entries[tool.name] === undefined ? tool.name : `${tool.name} (${idx})`;
      entries[key] = tool.description;
    });
    return entries;
  }, [tools]);

  return (
    <>
      <MetaDataGrid
        entries={toolEntries}
        options={{ plain: true }}
        className={styles.toolConfig}
      />
      <div className={clsx(styles.toolChoice, "text-size-small")}>
        <div className={clsx("text-style-label", "text-style-secondary")}>
          Tool Choice
        </div>
        <div>
          <ToolChoiceView toolChoice={toolChoice} />
        </div>
      </div>
    </>
  );
};

interface ToolChoiceViewProps {
  toolChoice: ToolChoice;
}

const ToolChoiceView: FC<ToolChoiceViewProps> = ({ toolChoice }) => {
  if (typeof toolChoice === "string") {
    return toolChoice;
  } else {
    return <code>`${toolChoice.name}()`</code>;
  }
};

// A bridged agent's own calls have no tool event, so they stay inline.
const withoutToolCallsIn = (
  message: ChatMessage,
  toolEventIds: ReadonlySet<string>
): ChatMessage => {
  if (message.role !== "assistant" || !message.tool_calls?.length) {
    return message;
  }
  const toolCalls = message.tool_calls.filter(
    (call) => !toolEventIds.has(call.id)
  );
  return toolCalls.length === message.tool_calls.length
    ? message
    : { ...message, tool_calls: toolCalls };
};
